import type { DifficultyProfile } from '../config/difficulty';
import { TUNING } from '../config/tuning';

export type AIIntent = 'wait' | 'start-inhale' | 'end-inhale' | 'pass';

export interface AIGameState {
  lungPct: number;
  chokePct: number;
  jointPct: number;
  myScore: number;
  opponentScore: number;
  /** Seconds already banked in this turn's combo — forfeited if choked/burned/lung-empty. */
  turnBankedSecs: number;
}

export interface QteBotContext {
  chokePct: number;
  jointPct: number;
  lungPct: number;
}

export interface IAIController {
  beginTurn(): void;
  endTurn(): void;
  notifyInhaleStarted(): void;
  /**
   * Called after an AI inhale ends. `reason` lets psyche-aware bots (Narco)
   * react to outcomes — e.g., trigger caution after a choke or spike
   * confidence after a clean release. Optional to keep backward compat with
   * Easy/Medium/Hard controllers that don't care.
   */
  notifyInhaleEnded(reason?: 'released' | 'choked' | 'joint-burned' | 'lung-empty'): void;
  update(deltaMs: number, state: AIGameState): AIIntent;
  setProfile(p: DifficultyProfile): void;
  /**
   * QTE armed — the current inhale started with lungs >= eligibility. Bot decides
   * whether it'll commit to reaching the QTE window (relaxing its normal stop criteria)
   * or release early per its usual logic.
   */
  onQteArmed(): void;
  /**
   * QTE activated — the line is now sweeping. Bot schedules its release attempt
   * (if it rolled to attempt) at a time offset from QTE start. `goldenStartPct`/
   * `goldenEndPct` are normalized bar positions (0..1 from left).
   */
  scheduleQteRelease(
    ctx: QteBotContext,
    durationMs: number,
    goldenStartPct: number,
    goldenEndPct: number,
  ): void;
  /**
   * QTE resolved — called after the bot's own QTE attempt, win or miss. Lets
   * the bot react to the newly-freed choke headroom: on a win it may extend
   * its turn with extra combo puffs instead of passing.
   */
  onQteResolved(result: { won: boolean; chokePctAfter: number; pctReduced: number }): void;
}

type SubState = 'idle' | 'thinking' | 'inhaling' | 'combo-window';

const rand = (lo: number, hi: number) => lo + Math.random() * (hi - lo);

/** Box-Muller transform, gaussian sample with mean 0 stddev sigma. */
const gaussian = (sigma: number): number => {
  const u1 = Math.max(1e-9, Math.random());
  const u2 = Math.random();
  return sigma * Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
};

export class AIController implements IAIController {
  private profile: DifficultyProfile;
  private subState: SubState = 'idle';
  private waitTimerMs = 0;
  private currentDragSecs = 0;
  private targetDragSecs = 0;
  private combosTaken = 0;
  private targetCombos = 0;

  /** True if the bot rolled to attempt the active/upcoming QTE this inhale. */
  private qteSeeking = false;
  /** When non-null: release when lung drops to or below this lung % (matches
   *  how QteController drives the line — see QteController.getLinePct). */
  private qteReleaseLungPct: number | null = null;

  constructor(profile: DifficultyProfile) {
    this.profile = profile;
  }

  setProfile(p: DifficultyProfile): void {
    this.profile = p;
  }

  beginTurn(): void {
    this.subState = 'thinking';
    this.waitTimerMs = rand(this.profile.thinkingTimeMs[0], this.profile.thinkingTimeMs[1]);
    this.currentDragSecs = 0;
    this.combosTaken = 0;
    this.targetCombos = Math.round(rand(this.profile.combosPerTurn[0], this.profile.combosPerTurn[1]));
    this.targetDragSecs = rand(this.profile.targetDragSecs[0], this.profile.targetDragSecs[1]);
    this.qteSeeking = false;
    this.qteReleaseLungPct = null;
  }

  endTurn(): void {
    this.subState = 'idle';
    this.qteSeeking = false;
    this.qteReleaseLungPct = null;
  }

  notifyInhaleStarted(): void {
    this.subState = 'inhaling';
    this.currentDragSecs = 0;
    this.qteSeeking = false;
    this.qteReleaseLungPct = null;
  }

  notifyInhaleEnded(_reason?: 'released' | 'choked' | 'joint-burned' | 'lung-empty'): void {
    this.qteSeeking = false;
    this.qteReleaseLungPct = null;
    this.combosTaken += 1;
    if (this.combosTaken >= this.targetCombos) {
      this.subState = 'idle';
      return;
    }
    this.subState = 'combo-window';
    this.waitTimerMs = rand(this.profile.comboWindowDelayMs[0], this.profile.comboWindowDelayMs[1]);
    this.targetDragSecs = rand(this.profile.targetDragSecs[0], this.profile.targetDragSecs[1]);
  }

  /** QTE win/miss — after the inhale has ended and choke has been reduced.
   *  On a win the bot may claim the freed headroom with one or more bonus
   *  puffs by extending targetCombos. Only triggers when comfortable margin
   *  exists: choke well below the ceiling AND at least one combo slot left. */
  onQteResolved(result: { won: boolean; chokePctAfter: number; pctReduced: number }): void {
    if (!result.won) return;
    const comfortCeiling = this.profile.chokeMeterCeiling;
    const headroom = comfortCeiling - result.chokePctAfter;
    // Need at least a meaningful gap to bother taking another puff; the exact
    // threshold is half the QTE's min reward — tight enough to be worth it.
    if (headroom < 8) return;
    // Grant up to 2 bonus puffs based on how much headroom we got back.
    const bonus = headroom >= 20 ? 2 : 1;
    this.targetCombos += bonus;
  }

  /** QTE armed: decide once whether this inhale is a QTE attempt. Seeking = relaxed stops. */
  onQteArmed(): void {
    this.qteSeeking = Math.random() < this.profile.qteAttemptPct;
  }

  scheduleQteRelease(
    _ctx: QteBotContext,
    _durationMs: number,
    goldenStartPct: number,
    goldenEndPct: number,
  ): void {
    if (!this.qteSeeking) {
      this.qteReleaseLungPct = null;
      return;
    }
    // QteController.getLinePct maps lung% → bar%: linePct = lung/span (where
    // span = trigger-end, lung taken relative to sweepEnd). Invert for center:
    // lung_at_center = end + linePct_center × span.
    const trigger = TUNING.qte.triggerLungPct;
    const end = TUNING.qte.sweepEndLungPct;
    const span = trigger - end;
    const goldenCenter = (goldenStartPct + goldenEndPct) / 2;
    const idealLung = end + goldenCenter * span;
    // qteAimJitterMs is still expressed in milliseconds; convert to a lung
    // offset by assuming the inhale drains at the profile's average rate. A
    // rough cap of ±half the zone width keeps the aim sensible.
    const avgDrainPctPerSec = TUNING.lung.inhaleDepletionPctPerSec * 2;
    const jitterLung = gaussian(this.profile.qteAimJitterMs / 1000) * avgDrainPctPerSec;
    const halfZone = (TUNING.qte.goldenZoneWidthPct / 100) * span * 0.5;
    const clamped = Math.max(-halfZone * 2, Math.min(halfZone * 2, jitterLung));
    this.qteReleaseLungPct = idealLung + clamped;
  }

  update(deltaMs: number, state: AIGameState): AIIntent {
    if (this.subState === 'idle') return 'pass';

    if (this.subState === 'thinking') {
      this.waitTimerMs -= deltaMs;
      if (this.waitTimerMs <= 0 && state.lungPct > 25) {
        return 'start-inhale';
      }
      return 'wait';
    }

    if (this.subState === 'inhaling') {
      this.currentDragSecs += deltaMs / 1000;

      // QTE release — bot waits for lungs to drain past its aimed target.
      // Hard safety still applies.
      if (this.qteReleaseLungPct !== null) {
        if (state.lungPct <= 3) { this.qteReleaseLungPct = null; return 'end-inhale'; }
        if (state.jointPct <= 1.5) { this.qteReleaseLungPct = null; return 'end-inhale'; }
        if (state.chokePct >= 99) { this.qteReleaseLungPct = null; return 'end-inhale'; }
        if (state.lungPct <= this.qteReleaseLungPct) {
          this.qteReleaseLungPct = null;
          return 'end-inhale';
        }
        return 'wait';
      }

      // QTE-seeking: bot is trying to reach the QTE window, suppress soft stops.
      // Only hard safety (near-forced lung/choke/cardboard) applies.
      if (this.qteSeeking) {
        if (state.lungPct <= 5) return 'end-inhale';
        if (state.jointPct <= 1.5) return 'end-inhale';
        if (state.chokePct >= 95) return 'end-inhale';
        return 'wait';
      }

      // Normal release criteria.
      if (this.currentDragSecs >= this.targetDragSecs) return 'end-inhale';
      if (state.chokePct >= this.profile.chokeMeterCeiling) return 'end-inhale';
      if (state.lungPct <= 8) return 'end-inhale';
      if (state.jointPct <= this.profile.jointAvoidanceMargin && state.jointPct > 1) return 'end-inhale';
      return 'wait';
    }

    if (this.subState === 'combo-window') {
      this.waitTimerMs -= deltaMs;
      if (this.waitTimerMs <= 0) {
        if (state.lungPct > 35 && this.combosTaken < this.targetCombos) {
          return 'start-inhale';
        }
        return 'pass';
      }
      return 'wait';
    }

    return 'wait';
  }
}
